import { useSyncExternalStore } from 'react';
import { submitFeedback, describeFeedbackError, type FeedbackType } from '../services/feedback-api';

/**
 * HQ Feedback draft store. HQ has no zustand, so this is a tiny external store read with useSyncExternalStore. The behaviour is the
 * same as the Feedback draft stores in the sibling applications, so the experience feels identical everywhere (no code is shared):
 *
 *  - the in-progress report lives in a store (not component state) so closing/reopening the panel or navigating never loses what
 *    was typed;
 *  - the duplicate-submit guard is SYNCHRONOUS (`submitting` is read and set inside one call), so a double click or a held Enter
 *    cannot start a second request before React re-renders;
 *  - retries reuse one `clientRequestId`, so the server recognises them as the same report; an edited draft (text or type) gets a
 *    fresh key so a lost-reply success can never swallow the edit;
 *  - a failed send keeps EVERYTHING the person typed, and the key;
 *  - a 409 REQUEST_CONFLICT (an earlier report exists under this key with different text) is shown honestly: the new text was NOT
 *    saved, the draft is kept, and the next Send is an explicit new report.
 */

export interface FeedbackConfirmation {
  reference: string;
  replayed: boolean;
}

function newRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // Fallback for very old runtimes: collision-resistant enough for a per-draft idempotency key.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
}

export const MAX_FEEDBACK_DETAILS = 4000;

export interface FeedbackDraftState {
  type: FeedbackType;
  details: string;
  clientRequestId: string;
  /** type + details of the last attempt that was sent, so an edited draft gets a fresh key. */
  lastAttempt: string | null;
  submitting: boolean;
  error: string | null;
  /** Set when the server says an EARLIER report already exists under this request id with different text. */
  conflictReference: string | null;
  confirmation: FeedbackConfirmation | null;
  setType: (t: FeedbackType) => void;
  setDetails: (d: string) => void;
  submit: (pagePath: string) => Promise<boolean>;
  startAnother: () => void;
}

const listeners = new Set<() => void>();
let state: FeedbackDraftState;

function get(): FeedbackDraftState {
  return state;
}
function set(partial: Partial<FeedbackDraftState>): void {
  state = { ...state, ...partial };
  listeners.forEach((l) => l());
}
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

async function submit(pagePath: string): Promise<boolean> {
  const s = get();
  // Synchronous duplicate guard: a second call while one is in flight does nothing.
  if (s.submitting) return false;
  const details = s.details.trim();
  if (!details || details.length > MAX_FEEDBACK_DETAILS) return false;

  // Same text + same type as a previous attempt => same key (a retry). Edited => new key.
  const fingerprint = `${s.type}\u0000${details}`;
  const clientRequestId = s.lastAttempt !== null && s.lastAttempt !== fingerprint ? newRequestId() : s.clientRequestId;
  set({ submitting: true, error: null, conflictReference: null, confirmation: null, clientRequestId, lastAttempt: fingerprint });

  try {
    const res = await submitFeedback({ type: s.type, details, clientRequestId, pagePath });
    // Confirmed only now, after the server has persisted the report. The draft is cleared and a fresh key is issued so the NEXT
    // report is never mistaken for a retry of this one.
    set({
      submitting: false, details: '', type: 'Bug', lastAttempt: null, clientRequestId: newRequestId(),
      confirmation: { reference: res.reference, replayed: res.replayed }, error: null, conflictReference: null,
    });
    return true;
  } catch (e) {
    const err = e as { status?: number; code?: string; body?: { reference?: string } };
    if (err?.status === 409 && err.code === 'REQUEST_CONFLICT') {
      // The server already holds an EARLIER report under this key with different text (a reply was lost, then the draft was
      // edited). The new text is NOT saved. Keep the draft untouched, say exactly that, and take a fresh key so the next Send is
      // an explicit new report.
      set({
        submitting: false, error: describeFeedbackError(e), conflictReference: err.body?.reference || 'an earlier report',
        clientRequestId: newRequestId(), lastAttempt: null,
      });
      return false;
    }
    // Failure: keep EVERYTHING the user typed (and the key, so a retry cannot duplicate).
    set({ submitting: false, error: describeFeedbackError(e) });
    return false;
  }
}

state = {
  type: 'Bug',
  details: '',
  clientRequestId: newRequestId(),
  lastAttempt: null,
  submitting: false,
  error: null,
  conflictReference: null,
  confirmation: null,

  setType: (type) => set({ type, error: null, conflictReference: null, confirmation: null }),
  setDetails: (details) => set({ details, error: null, conflictReference: null, confirmation: null }),
  submit,
  startAnother: () => set({ confirmation: null, error: null, conflictReference: null }),
};

/** React hook: the current draft state (with its actions). Pass a selector to subscribe to one field. */
export function useFeedbackDraft(): FeedbackDraftState;
export function useFeedbackDraft<T>(selector: (s: FeedbackDraftState) => T): T;
export function useFeedbackDraft<T>(selector?: (s: FeedbackDraftState) => T): FeedbackDraftState | T {
  return useSyncExternalStore(subscribe, () => (selector ? selector(get()) : get()));
}

/** Non-React access (tests, imperative callers). */
export const feedbackDraft = { getState: get };
